import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Compass, Circle, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Small dashboard widget that shows the consultant's CURRENT WEEK focus
 * from the 4-1-1 GPS: monthly goal + this week's commitments with checkable
 * done state, plus a % progress.
 */
export default function WeekFocusWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => {
    api.get("/gps/my-focus").then((r) => setData(r.data)).catch(() => setData({ has_focus: false }));
  };

  useEffect(() => { load(); }, []);

  const toggle = async (idx) => {
    if (!data?.has_focus) return;
    const c = data.commitments[idx];
    if (!(c || "").trim()) return;
    // Optimistic UI: update local state first, then persist by fetching current
    // year/month/week and rewriting only the toggled done flag through PUT /gps/411.
    const newDone = [...data.done];
    newDone[idx] = !newDone[idx];
    setData({ ...data, done: newDone });
    try {
      const { data: full } = await api.get(`/gps/411?year=${data.year}`);
      const months = full.data.months;
      const m = months[data.month_idx];
      const w = m.weeks[data.week_idx];
      w.done = newDone.slice(0, (w.commitments || []).length);
      await api.put(`/gps/411?year=${data.year}`, { months });
      load();
    } catch {
      // revert on error
      load();
    }
  };

  if (!data) return null;

  // Admin users without a player linked: silently hide widget.
  if (!data.has_focus && user?.role === "admin") return null;

  if (!data.has_focus) {
    return (
      <div className="kw-card p-5 flex items-center gap-4" data-testid="week-focus-empty">
        <Compass className="w-6 h-6 text-[#E50000] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline">Foco Semanal · GPS 4-1-1</div>
          <div className="text-sm text-[#A3A3A3] mt-1">
            Ainda não configuraste o teu GPS. Define os teus compromissos semanais para
            aparecerem aqui.
          </div>
        </div>
        <Link to="/my-profile" className="kw-btn-secondary text-xs inline-flex items-center gap-2" data-testid="week-focus-setup-btn">
          Configurar GPS <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const nonEmpty = (data.commitments || []).map((c, i) => ({ c, i })).filter((x) => (x.c || "").trim());

  return (
    <div className="kw-card p-5" data-testid="week-focus-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-[#E50000]" />
          <div>
            <div className="kw-overline">Foco Semanal · GPS 4-1-1</div>
            <div className="font-display text-lg font-black uppercase">
              {data.monthly_goal || <span className="text-[#737373] normal-case text-sm">Sem objetivo mensal definido</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-widest text-[#737373]">Progresso</div>
          <div className="font-display text-2xl font-black" style={{ color: data.done_pct >= 60 ? "#22C55E" : data.done_pct >= 30 ? "#EAB308" : "#E50000" }}>
            {data.done_pct}%
          </div>
          <Link to="/my-profile#gps" className="kw-btn-secondary text-xs inline-flex items-center gap-1" data-testid="week-focus-open">
            Abrir GPS <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      {nonEmpty.length === 0 ? (
        <div className="text-xs text-[#737373] italic">Sem compromissos para esta semana. Configura o teu GPS.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
          {nonEmpty.map(({ c, i }) => (
            <button
              key={i}
              onClick={() => toggle(i)}
              className={`text-left border p-2.5 flex items-start gap-2 transition-colors ${
                data.done[i] ? "border-[#22C55E]/40 bg-[#22C55E]/5" : "border-[#333] hover:border-[#555]"
              }`}
              data-testid={`week-focus-commit-${i}`}
            >
              {data.done[i] ? (
                <CheckCircle2 className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-4 h-4 text-[#666] flex-shrink-0 mt-0.5" />
              )}
              <div className={`text-xs leading-snug flex-1 ${data.done[i] ? "line-through text-[#888]" : ""}`}>
                {c}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
